import { api } from "../scripts/api.js";

/*
=============================================================================
   <app-my-xp>: the student's own XP page                          (2026-10-22)
=============================================================================
Coach Brown asked for "an XP guide for the students so they can see how they are
doing and understand where they got points", with the leaderboards. Four tabs:

   Overview      level, this week, season, streak (and what keeps it), medals, gear,
                 today's challenge, skills, Next up
   Where my XP   every award with its pack, tab and class, totals by kind and period,
   came from     and the tabs that paid NOTHING with the reason (guide opened, a check
                 failed, skipped, not cleared). That last list is the answer to most
                 "why didn't I get XP?" questions before they reach a coach.
   Leaderboards  weekly / season / squads for each of the student's Game Launcher
                 classes, nicknames only (the server decides that), "You" highlighted
   How XP works  written FROM THE LIVE RULES the server sends, so a rule change on the
                 server changes this page and the two cannot drift apart

Reads GET /me/xp/profile (which syncs and pays skill-met and daily XP), then
GET /me/xp/history, then GET /classes/:id/xp/board per class on demand. Identity
comes from the session; nothing here can ask for another student's ledger.

Hash routes: #xp, #xp/history, #xp/boards, #xp/rules.
=============================================================================
*/

const TABS = [
   { key: "overview", label: "Overview" },
   { key: "history", label: "Where my XP came from" },
   { key: "boards", label: "Leaderboards" },
   { key: "rules", label: "How XP works" },
];

// Every award code the server writes, grouped the way a student thinks about them.
const KINDS = {
   clear:        { label: "Tab clears", group: "play", color: "#1f7a4d" },
   pb:           { label: "Personal bests", group: "bonus", color: "#2b6cb0" },
   new_area:     { label: "First clear in a skill area", group: "bonus", color: "#6b46c1" },
   new_tier:     { label: "New tier in a skill area", group: "bonus", color: "#805ad5" },
   streak:       { label: "Daily streak", group: "bonus", color: "#dd6b20" },
   pack:         { label: "Packs finished", group: "bonus", color: "#2c7a7b" },
   daily:        { label: "Daily challenge", group: "bonus", color: "#c05621" },
   skill_met:    { label: "Skills met", group: "bonus", color: "#0c234b" },
   weekly_top:   { label: "Weekly top 3", group: "team", color: "#ab0520" },
   weekly_squad: { label: "Weekly top squad", group: "team", color: "#c53030" },
   practice:     { label: "Friday practice", group: "team", color: "#b7791f" },
   sprint:       { label: "Practice sprints", group: "team", color: "#975a16" },
   team_win:     { label: "Team wins", group: "team", color: "#744210" },
   invitational: { label: "Invitationals", group: "team", color: "#9b2c2c" },
   other:        { label: "Other coach awards", group: "team", color: "#4a5568" },
};
const kindOf = (code) => KINDS[code] || { label: code, group: "team", color: "#4a5568" };

const AZ = "America/Phoenix";
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const num = (n) => Number(n || 0).toLocaleString("en-US");
const azDay = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: AZ });          // YYYY-MM-DD
const dayLabel = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const timeLabel = (d) => new Date(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: AZ });
const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MEDAL = { gold: "Gold", silver: "Silver", bronze: "Bronze" };

class AppMyXp extends HTMLElement {
   connectedCallback() {
      this.tab = "overview";
      this.period = "week";
      this.histShown = 60;
      this.boardKind = "weekly";
      this.boards = new Map();
      this.innerHTML = `
         <section id="my-xp">
            <header class="classes-header"><h1>My XP</h1></header>
            <p class="mx-intro">How you are doing in the Game Launcher, where every point came from, and where you stand.
               XP has nothing to do with your grade.</p>
            <nav class="mx-tabs" role="tablist">${TABS.map((t) =>
               `<a role="tab" class="mx-tab" data-tab="${t.key}" href="#xp${t.key === "overview" ? "" : "/" + t.key}">${t.label}</a>`).join("")}</nav>
            <p class="msg" id="mx-msg"></p>
            <div id="mx-body"></div>
         </section>`;
      this.querySelector("#mx-body").addEventListener("click", (e) => this.onClick(e));
      this.querySelector("#mx-body").addEventListener("change", (e) => this.onChange(e));
   }

   /* Called by the router each time #xp... is opened: refetch, so the page is never stale. */
   show(param) {
      this.tab = TABS.some((t) => t.key === param) ? param : "overview";
      this.querySelectorAll(".mx-tab").forEach((a) => a.classList.toggle("on", a.dataset.tab === this.tab));
      const fresh = this.loadedAt && Date.now() - this.loadedAt < 15000;
      if (fresh) this.render();
      else this.load();
   }

   async load() {
      const msg = this.querySelector("#mx-msg");
      if (!this.data) this.querySelector("#mx-body").innerHTML = `<p class="mx-muted">Loading your XP...</p>`;
      try {
         const pr = await api("/me/xp/profile");          // syncs first, so history is current
         const profile = await pr.json();
         if (!pr.ok || !profile.ok) throw new Error(profile.reason || "profile");
         const hr = await api("/me/xp/history");
         const history = await hr.json();
         if (!hr.ok || !history.ok) throw new Error(history.reason || "history");
         this.data = { profile, history };
         this.boards.clear();
         this.loadedAt = Date.now();
         msg.textContent = ""; msg.className = "msg";
         this.render();
      } catch (err) {
         msg.textContent = this.data ? "Could not refresh just now; this is what was loaded earlier."
            : "Could not load your XP. Try again in a moment.";
         msg.className = "msg err";
         if (!this.data) this.querySelector("#mx-body").innerHTML = "";
      }
   }

   render() {
      if (!this.data) return;
      const body = this.querySelector("#mx-body");
      const { history } = this.data;
      if (!history.classes.length && !history.awards.length) {
         body.innerHTML = `<div class="mx-card"><p>None of your classes uses the Game Launcher yet, so there is no XP to show.
            When a class turns it on, everything you play there shows up here.</p></div>`;
         return;
      }
      body.innerHTML = this.tab === "history" ? this.historyHtml()
         : this.tab === "boards" ? this.boardsHtml()
         : this.tab === "rules" ? this.rulesHtml()
         : this.overviewHtml();
      if (this.tab === "boards") this.loadBoard();
   }

   onClick(e) {
      const b = e.target.closest("[data-act]");
      if (!b) return;
      const act = b.dataset.act;
      if (act === "period") { this.period = b.dataset.v; this.render(); }
      else if (act === "more") { this.histShown += 100; this.render(); }
      else if (act === "board") { this.boardKind = b.dataset.v; this.render(); }
      else if (act === "unscored") { this.showAllUnscored = true; this.render(); }
   }

   onChange(e) {
      if (e.target.id === "mx-class") { this.boardClass = Number(e.target.value); this.render(); }
   }

   // ── shared bits ─────────────────────────────────────────────────────────
   weekStartDay() { return azDay(this.data.history.week_start); }
   seasonStartDay() { return azDay(this.data.history.season_start); }
   inPeriod(a, period) {
      if (period === "all") return true;
      const d = azDay(a.earned_at);
      if (period === "season") return d >= this.seasonStartDay();
      // the weekly board leaves out the weekly prizes themselves, and so does "this week" here
      return d >= this.weekStartDay() && !this.data.history.weekly_codes.includes(a.code);
   }
   weekRange() {
      const start = this.weekStartDay();
      const end = new Date(Date.parse(`${start}T12:00:00Z`) + 6 * 86400000).toISOString().slice(0, 10);
      return `${dayLabel(start)} to ${dayLabel(end)}`;
   }
   streakInfo() {
      const { profile, history } = this.data;
      const R = history.rules, per = Number(R.daily_streak_bonus_per_day || 0), cap = Number(R.daily_streak_cap_days || 7);
      const pay = (day) => (day > 1 ? per * Math.min(day, cap) : 0);
      const today = history.today, last = history.last_clean_day, n = Number(profile.streak || 0);
      const playedToday = last === today;
      if (playedToday) return { n, playedToday, line: `Today counts. Clear a tab tomorrow for +${num(pay(n + 1))} XP (day ${n + 1}).` };
      if (n > 0) return { n, playedToday, line: `Clear a tab cleanly today to keep it: +${num(pay(n + 1))} XP (day ${n + 1}). Miss today and it starts over.` };
      return { n: 0, playedToday, line: `Clear a tab today to start one. Day 2 pays +${num(pay(2))} XP and it grows to +${num(pay(cap))} a day.` };
   }
   whatHtml(a) {
      const bits = [];
      // a tab award names the tab and its pack; a pack bonus or a bonus row leads with its own detail
      if (a.tab) bits.push(`<span class="mx-what-main">${esc(a.tab)}</span> <span class="mx-muted">in ${esc(a.pack_title || "a pack")}</span>`);
      else bits.push(`<span class="mx-what-main">${esc(a.detail || kindOf(a.code).label)}</span>`);
      const sub = [];
      if (a.tab && a.detail) sub.push(esc(a.detail));
      if (a.coach) sub.push(`from ${esc(a.coach)}`);
      if (a.class_title) sub.push(esc(a.class_title));
      return `${bits.join("")}${sub.length ? `<div class="mx-sub">${sub.join(" · ")}</div>` : ""}`;
   }
   chip(code) {
      const k = kindOf(code);
      return `<span class="mx-chip" style="--c:${k.color}">${esc(k.label)}</span>`;
   }
   medalBadge(m) { return m ? `<span class="mx-medal mx-${m}">${MEDAL[m] || esc(m)}</span>` : ""; }

   // ── Overview ────────────────────────────────────────────────────────────
   overviewHtml() {
      const { profile: p, history: h } = this.data;
      const pct = p.xp_for_level ? Math.max(2, Math.min(100, Math.round((100 * p.xp_into_level) / p.xp_for_level))) : 0;
      const titles = h.rules.titles || [];
      const nextTitle = titles.find(([lv]) => lv > p.level);
      const st = this.streakInfo();
      const m = p.medals || {};
      const byKind = this.totals(h.awards.filter((a) => this.inPeriod(a, "season")));
      const seasonTotal = byKind.reduce((t, k) => t + k.xp, 0) || 1;
      return `
         <div class="mx-hero">
            <div class="mx-hero-top">
               <div><div class="mx-lv">Level ${p.level}</div><div class="mx-title">${esc(p.title)}</div></div>
               <div class="mx-total"><span>${num(p.xp)}</span> XP</div>
            </div>
            <div class="mx-bar" title="${num(p.xp_into_level)} of ${num(p.xp_for_level)}"><div class="mx-fill" style="width:${pct}%"></div></div>
            <div class="mx-hero-sub">${num(p.xp_for_level - p.xp_into_level)} XP to level ${p.level + 1}${nextTitle ? ` · ${esc(nextTitle[1])} at level ${nextTitle[0]}` : ""}</div>
         </div>

         <div class="mx-tiles">
            <div class="mx-tile"><div class="mx-k">This week</div><div class="mx-v">${num(p.week_xp)} XP</div><div class="mx-s">${this.weekRange()}</div></div>
            <div class="mx-tile"><div class="mx-k">${esc(p.season_name || "This season")}</div><div class="mx-v">${num(p.season_xp)} XP</div><div class="mx-s">the season board</div></div>
            <div class="mx-tile mx-tile-streak${st.n > 1 ? " hot" : ""}"><div class="mx-k">Daily streak</div>
               <div class="mx-v">${st.n > 1 ? "🔥 " : ""}${st.n} day${st.n === 1 ? "" : "s"}${st.playedToday ? ` <span class="mx-ok">✓ today</span>` : ""}</div>
               <div class="mx-s">${esc(st.line)}</div></div>
            <div class="mx-tile"><div class="mx-k">Medals</div>
               <div class="mx-v mx-medals">${this.medalBadge("gold")} ${num(m.gold)} ${this.medalBadge("silver")} ${num(m.silver)} ${this.medalBadge("bronze")} ${num(m.bronze)}</div>
               <div class="mx-s">${num(p.clean_clears)} clean of ${num(p.attempts)} tabs tried · ${num(p.days_played)} days played</div></div>
         </div>

         ${this.dailyHtml(p)}
         ${this.gearHtml(p)}

         <div class="mx-grid2">
            <div class="mx-card">
               <h2>Where your season XP came from</h2>
               ${byKind.length ? byKind.slice(0, 6).map((k) => `
                  <div class="mx-brow"><span class="mx-blabel">${esc(k.label)}</span>
                     <span class="mx-btrack"><span class="mx-bfill" style="width:${Math.max(2, Math.round((100 * k.xp) / seasonTotal))}%;background:${k.color}"></span></span>
                     <span class="mx-bnum">${num(k.xp)}</span></div>`).join("")
                  : `<p class="mx-muted">Nothing yet this season.</p>`}
               <p class="mx-link"><a href="#xp/history">See every point, and tabs that earned nothing →</a></p>
            </div>
            <div class="mx-card">
               <h2>Latest</h2>
               ${h.awards.slice(0, 6).map((a) => `
                  <div class="mx-recent"><span class="mx-plus">+${num(a.points)}</span>
                     <span class="mx-rwhat">${a.tab ? `${esc(kindOf(a.code).label)}: ${esc(a.tab)}` : esc(a.detail || kindOf(a.code).label)}</span>
                     <span class="mx-muted mx-rwhen">${dayLabel(azDay(a.earned_at))}</span></div>`).join("") || `<p class="mx-muted">No XP yet. Clear a tab in the Game Launcher to get started.</p>`}
            </div>
         </div>

         <div class="mx-card">
            <h2>Your skill areas</h2>
            <p class="mx-note">The highest tier you have cleared cleanly in each area, and its best medal.</p>
            <div class="mx-areas">${(p.areas || []).map((a) => `
               <div class="mx-area mx-area-${a.status}${a.medal ? ` mx-${a.medal}-bg` : ""}">
                  <div class="mx-aname">${esc(a.area)}</div>
                  <div class="mx-astat">${a.status === "cleared" ? `Tier ${a.tier} · ${MEDAL[a.medal] || ""}` : a.status === "started" ? "Started, not cleared" : "Untested"}</div>
               </div>`).join("")}</div>
         </div>

         ${(p.next_up || []).length ? `<div class="mx-card"><h2>Next up</h2>
            <p class="mx-note">Picked for you from your skill areas. Launch them from the Game Launcher in the add-in.</p>
            ${p.next_up.map((n) => `<div class="mx-next"><span class="mx-reason mx-r-${esc(n.reason)}">${esc(String(n.reason || "").replace("_", " "))}</span>
               <span class="mx-what-main">${esc(n.title)}</span><div class="mx-sub">${esc(n.why)}</div></div>`).join("")}</div>` : ""}`;
   }

   dailyHtml(p) {
      const list = Array.isArray(p.daily) ? p.daily : [];
      if (!list.length) return "";
      return `<div class="mx-card mx-daily"><h2>Today's challenge</h2>${list.map((d) => `
         <div class="mx-next"><span class="mx-reason ${d.done ? "mx-r-done" : "mx-r-daily"}">${d.done ? "✓ done" : `${esc(d.multiplier)}x XP`}</span>
            <span class="mx-what-main">${esc(d.title)}</span>
            <div class="mx-sub">${d.done ? `Bonus earned: +${num(d.bonus)} XP. A new one tomorrow.`
               : "Finish it today and its tab XP pays again. Any clean clear keeps your streak, this one or any other."}</div></div>`).join("")}</div>`;
   }

   gearHtml(p) {
      const g = p.gear;
      if (!g || !g.tiers) return "";
      return `<div class="mx-card"><h2>Team gear${g.tentative ? ` <span class="mx-tent">tiers are tentative</span>` : ""}</h2>
         <p class="mx-note">Earned from your lifetime XP plus Friday practices attended (${num(p.fridays)} so far).</p>
         <div class="mx-gear">${g.tiers.map((t) => {
            const xpPct = t.xp ? Math.min(100, Math.round((100 * p.xp) / t.xp)) : 100;
            const frPct = t.fridays ? Math.min(100, Math.round((100 * p.fridays) / t.fridays)) : 100;
            return `<div class="mx-gtier${t.earned ? " earned" : ""}">
               <div class="mx-gname">${esc(t.label)}${t.earned ? ` <span class="mx-ok">${t.handed_out ? "✓ handed out" : "✓ earned"}</span>` : ""}</div>
               <div class="mx-gline"><span>${num(Math.min(p.xp, t.xp))} / ${num(t.xp)} XP</span><span class="mx-btrack"><span class="mx-bfill" style="width:${xpPct}%"></span></span></div>
               ${t.fridays ? `<div class="mx-gline"><span>${num(Math.min(p.fridays, t.fridays))} / ${num(t.fridays)} Fridays</span><span class="mx-btrack"><span class="mx-bfill" style="width:${frPct}%"></span></span></div>` : ""}
            </div>`; }).join("")}</div></div>`;
   }

   totals(list) {
      const by = new Map();
      for (const a of list) {
         const k = kindOf(a.code);
         const o = by.get(k.label) || { label: k.label, color: k.color, group: k.group, xp: 0, n: 0 };
         o.xp += a.points; o.n++;
         by.set(k.label, o);
      }
      return [...by.values()].sort((a, b) => b.xp - a.xp);
   }

   // ── Where my XP came from ───────────────────────────────────────────────
   historyHtml() {
      const h = this.data.history;
      const list = h.awards.filter((a) => this.inPeriod(a, this.period));
      const kinds = this.totals(list);
      const total = list.reduce((t, a) => t + a.points, 0);
      const periods = [["week", `This week (${this.weekRange()})`], ["season", this.data.profile.season_name || "This season"], ["all", "All time"]];
      const shown = list.slice(0, this.histShown);
      const days = new Map();
      for (const a of shown) { const d = azDay(a.earned_at); if (!days.has(d)) days.set(d, []); days.get(d).push(a); }
      const dayTotals = new Map();
      for (const a of list) { const d = azDay(a.earned_at); dayTotals.set(d, (dayTotals.get(d) || 0) + a.points); }
      const uns = this.showAllUnscored ? h.unscored : h.unscored.slice(0, 8);
      return `
         <div class="mx-seg">${periods.map(([k, l]) => `<button type="button" data-act="period" data-v="${k}" class="${this.period === k ? "on" : ""}">${esc(l)}</button>`).join("")}</div>
         <div class="mx-card">
            <h2>${num(total)} XP ${this.period === "week" ? "this week" : this.period === "season" ? "this season" : "in all"}</h2>
            ${this.period === "week" ? `<p class="mx-note">Weekly prizes are left out here, the same as on the weekly board, so last week's winners do not start this week ahead.</p>` : ""}
            ${kinds.length ? `<table class="cd-table mx-ktable"><thead><tr><th>Kind</th><th class="mx-num">How many</th><th>Share</th><th class="mx-num">XP</th></tr></thead><tbody>
               ${kinds.map((k) => `<tr><td>${this.chip(Object.keys(KINDS).find((c) => KINDS[c].label === k.label) || "other")}</td>
                  <td class="mx-num">${num(k.n)}</td>
                  <td><span class="mx-btrack mx-wide"><span class="mx-bfill" style="width:${Math.max(2, Math.round((100 * k.xp) / (total || 1)))}%;background:${k.color}"></span></span></td>
                  <td class="mx-num"><strong>${num(k.xp)}</strong></td></tr>`).join("")}</tbody></table>`
               : `<p class="mx-muted">No XP in this period.</p>`}
         </div>

         <div class="mx-card">
            <h2>Every award</h2>
            <p class="mx-note">Newest first. Times are Arizona time; a day runs midnight to midnight.</p>
            ${[...days.entries()].map(([d, rows]) => `
               <div class="mx-day"><span>${dayLabel(d)}</span><span class="mx-daytot">+${num(dayTotals.get(d))} XP</span></div>
               <table class="mx-htable"><tbody>${rows.map((a) => `
                  <tr><td class="mx-htime">${timeLabel(a.earned_at)}</td>
                     <td class="mx-hkind">${this.chip(a.code)}</td>
                     <td class="mx-hwhat">${this.whatHtml(a)}</td>
                     <td class="mx-hbadge">${a.tier ? `<span class="mx-tier">Tier ${a.tier}</span>` : ""}${this.medalBadge(a.medal)}</td>
                     <td class="mx-num mx-plus">+${num(a.points)}</td></tr>`).join("")}</tbody></table>`).join("") || `<p class="mx-muted">No XP in this period.</p>`}
            ${list.length > shown.length ? `<p><button type="button" class="mx-btn" data-act="more">Show more (${num(list.length - shown.length)} left)</button></p>` : ""}
         </div>

         <div class="mx-card">
            <h2>Tabs that earned no XP</h2>
            <p class="mx-note">Only a clean clear pays: cleared, every check passed, and the guide not opened. Exams never pay XP and are not listed.</p>
            ${h.unscored.length ? `<table class="cd-table"><thead><tr><th>When</th><th>Tab</th><th>Why no XP</th></tr></thead><tbody>
               ${uns.map((u) => `<tr><td class="mx-nowrap">${dayLabel(azDay(u.at))} ${timeLabel(u.at)}</td>
                  <td><span class="mx-what-main">${esc(u.tab)}</span> <span class="mx-muted">in ${esc(u.pack_title)}</span>${u.tier ? ` <span class="mx-tier">Tier ${u.tier}</span>` : ""}</td>
                  <td><span class="mx-why mx-why-${esc(u.why)}">${esc(u.text)}</span></td></tr>`).join("")}</tbody></table>
               ${!this.showAllUnscored && h.unscored.length > 8 ? `<p><button type="button" class="mx-btn" data-act="unscored">Show all ${num(h.unscored.length)}</button></p>` : ""}
               ${h.unscored_total > h.unscored.length ? `<p class="mx-note">Showing the latest ${num(h.unscored.length)} of ${num(h.unscored_total)}.</p>` : ""}`
               : `<p class="mx-muted">None. Every tab you have tried paid XP.</p>`}
         </div>`;
   }

   // ── Leaderboards ────────────────────────────────────────────────────────
   boardsHtml() {
      const h = this.data.history;
      if (!h.classes.length) return `<div class="mx-card"><p>None of your classes has a leaderboard.</p></div>`;
      if (!h.classes.some((c) => c.class_id === this.boardClass)) this.boardClass = h.classes[0].class_id;
      const kinds = [["weekly", "This week"], ["season", this.data.profile.season_name || "Season"], ["squads", "Squads"]];
      return `
         <div class="mx-controls">
            ${h.classes.length > 1 ? `<label class="mx-field">Class<select id="mx-class">${h.classes.map((c) =>
               `<option value="${c.class_id}"${c.class_id === this.boardClass ? " selected" : ""}>${esc(c.title)}</option>`).join("")}</select></label>` : ""}
            <div class="mx-seg">${kinds.map(([k, l]) => `<button type="button" data-act="board" data-v="${k}" class="${this.boardKind === k ? "on" : ""}">${esc(l)}</button>`).join("")}</div>
         </div>
         <div id="mx-board"><p class="mx-muted">Loading the board...</p></div>`;
   }

   async loadBoard() {
      const id = this.boardClass;
      let b = this.boards.get(id);
      if (!b) {
         try {
            const r = await api(`/classes/${id}/xp/board`);
            b = await r.json();
            if (!r.ok || !b.ok) throw new Error(b.reason || "board");
            this.boards.set(id, b);
         } catch {
            const el = this.querySelector("#mx-board");
            if (el) el.innerHTML = `<p class="msg err">Could not load this board. Try again in a moment.</p>`;
            return;
         }
      }
      const el = this.querySelector("#mx-board");
      if (el && this.tab === "boards" && this.boardClass === id) el.innerHTML = this.boardHtml(b);
   }

   boardHtml(b) {
      const me = this.data.history.user_id;
      const cls = this.data.history.classes.find((c) => c.class_id === this.boardClass) || {};
      const R = this.data.history.rules;
      if (this.boardKind === "squads") {
         const mySquad = (b.weekly.find((r) => r.user_id === me) || {}).squad;
         return `<div class="mx-card"><h2>Squads</h2>
            <p class="mx-note">Squad XP is the members' XP added up. Ranked by this week, then the season.</p>
            ${b.squads.length ? `<table class="cd-table"><thead><tr><th>#</th><th>Squad</th><th>Members</th><th class="mx-num">This week</th><th class="mx-num">Season</th></tr></thead><tbody>
               ${b.squads.map((q) => `<tr class="${q.squad === mySquad ? "mx-me" : ""}"><td>${q.rank}</td><td><strong>${esc(q.squad)}</strong>${q.squad === mySquad ? ` <span class="mx-you">your squad</span>` : ""}</td>
                  <td class="mx-sub">${q.members.map((m) => `${esc(m.name)}${m.position ? ` (${esc(m.position)})` : ""}`).join(", ")}</td>
                  <td class="mx-num">${num(q.week_xp)}</td><td class="mx-num">${num(q.season_xp)}</td></tr>`).join("")}</tbody></table>`
               : `<p class="mx-muted">No squads are set for this class.</p>`}
            ${cls.team && R.weekly_awards ? `<p class="mx-note">Every member of the top squad for the week gets ${num(R.weekly_awards.squad)} XP, awarded at Friday practice.</p>` : ""}</div>`;
      }
      const key = this.boardKind === "weekly" ? "week_xp" : "season_xp";
      const rows = b[this.boardKind === "weekly" ? "weekly" : "season"] || [];
      const mine = rows.find((r) => r.user_id === me);
      let where = "";
      if (mine) {
         const above = rows.filter((r) => r[key] > mine[key]).pop();
         where = `You are <strong>#${mine.rank}</strong> of ${rows.length}${above ? `, ${num(above[key] - mine[key])} XP behind #${above.rank}` : rows.length > 1 ? ", in first place" : ""}.`;
      }
      const prizes = cls.team && R.weekly_awards && this.boardKind === "weekly"
         ? ` The top 3 at the end of the week get ${R.weekly_awards.top.map(num).join(" / ")} XP, awarded at Friday practice.` : "";
      return `<div class="mx-card"><h2>${this.boardKind === "weekly" ? `This week · ${this.weekRange()}` : esc(b.season_name)}</h2>
         <p class="mx-note">${where} ${this.boardKind === "weekly"
            ? `The week runs ${DOW[b.week_start_dow] || ""} to ${DOW[(Number(b.week_start_dow) + 6) % 7] || ""}, Arizona time; work over the weekend counts toward the coming week.${prizes}`
            : "The season board counts this semester. Levels count everything, all time."} Boards show nicknames; set yours under My Profile.</p>
         <table class="cd-table mx-board"><thead><tr><th>#</th><th>Player</th><th>Level</th><th class="mx-num">${this.boardKind === "weekly" ? "This week" : "Season"}</th></tr></thead><tbody>
            ${rows.map((r) => `<tr class="${r.user_id === me ? "mx-me" : ""}${r[key] === 0 ? " mx-zero" : ""}">
               <td>${r.rank <= 3 && r[key] > 0 ? ["🥇", "🥈", "🥉"][r.rank - 1] : r.rank}</td>
               <td><strong>${esc(r.name)}</strong>${r.user_id === me ? ` <span class="mx-you">you</span>` : ""}${r.squad ? ` <span class="mx-sub">${esc(r.squad)}</span>` : ""}</td>
               <td class="mx-sub">${r.level} · ${esc(r.title)}</td>
               <td class="mx-num"><strong>${num(r[key])}</strong></td></tr>`).join("")}</tbody></table></div>`;
   }

   // ── How XP works (from the live rules) ──────────────────────────────────
   rulesHtml() {
      const h = this.data.history, R = h.rules;
      const tiers = Object.entries(R.xp_base_by_tier || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
      const mm = R.medal_multiplier || {};
      const decay = R.repeat_decay || [1];
      const per = Number(R.daily_streak_bonus_per_day || 0), cap = Number(R.daily_streak_cap_days || 7);
      const streakRows = Array.from({ length: cap + 1 }, (_, i) => i + 1).map((d) => [d, d > 1 ? per * Math.min(d, cap) : 0]);
      const f = Number(R.level_xp_factor || 50);
      const lvlXp = (L) => f * L * (L + 1);
      const ex = tiers.find(([t]) => t === "3") || tiers[0];
      const exPts = ex ? Math.round(Number(ex[1]) * Number(mm.silver || 1)) : 0;
      const team = h.team;
      const dow = DOW[h.week_start_dow] || "Monday", dowEnd = DOW[(Number(h.week_start_dow) + 6) % 7] || "Sunday";
      return `
         <div class="mx-card">
            <h2>1. Every clean tab pays XP</h2>
            <p>You earn XP in timed Game Launcher packs, one tab at a time. A tab pays only when it is a <strong>clean clear</strong>:</p>
            <ul class="mx-list"><li>you cleared the tab,</li><li>no extra check failed,</li><li>you did not open the tab's guide,</li><li>you did not skip it, and</li><li>it was not part of an exam (exams never pay XP).</li></ul>
            <p>What a clean tab pays is <strong>tier XP x medal</strong>:</p>
            <div class="mx-grid2">
               <table class="cd-table"><thead><tr><th>Tier</th><th class="mx-num">Base XP</th></tr></thead><tbody>
                  ${tiers.map(([t, x]) => `<tr><td>Tier ${esc(t)}</td><td class="mx-num">${num(x)}</td></tr>`).join("")}</tbody></table>
               <table class="cd-table"><thead><tr><th>Medal</th><th>How</th><th class="mx-num">x</th></tr></thead><tbody>
                  <tr><td>${this.medalBadge("bronze")}</td><td>Cleared</td><td class="mx-num">${esc(mm.bronze)}</td></tr>
                  <tr><td>${this.medalBadge("silver")}</td><td>At or under the median time for that tab</td><td class="mx-num">${esc(mm.silver)}</td></tr>
                  <tr><td>${this.medalBadge("gold")}</td><td>In the fastest ${esc(R.gold_percentile)}% of clears</td><td class="mx-num">${esc(mm.gold)}</td></tr></tbody></table>
            </div>
            <p class="mx-note">Medal times come from students' clean clears. Until a tab has ${esc(R.benchmark_min_clears)} of them, its par time stands in for the median and ${Math.round(Number(R.gold_vs_par_before_data || 0.5) * 100)}% of par is gold.</p>
            ${ex ? `<p class="mx-example">Example: a Tier ${esc(ex[0])} tab with a silver medal pays ${num(ex[1])} x ${esc(mm.silver)} = <strong>${num(exPts)} XP</strong>.</p>` : ""}
            ${decay.length > 1 ? `<p><strong>Repeats pay less.</strong> Your first clean clear of a tab pays in full. Clearing it again pays ${decay.slice(1).map((d) => `${Math.round(d * 100)}%`).join(", then ")}${decay.length > 2 ? " from then on" : ""}. New tabs pay the most.</p>` : ""}
         </div>

         <div class="mx-card">
            <h2>2. Bonuses</h2>
            <table class="cd-table"><thead><tr><th>Bonus</th><th>When</th><th class="mx-num">XP</th></tr></thead><tbody>
               <tr><td>${this.chip("pb")}</td><td>You clear a tab at least ${esc(R.personal_best_min_gain_pct)}% faster than your best time on it</td><td class="mx-num">${esc(R.personal_best_bonus_pct)}% of tier XP</td></tr>
               <tr><td>${this.chip("new_area")}</td><td>Your first clean clear in a skill area</td><td class="mx-num">+${num(R.new_area_bonus)}</td></tr>
               <tr><td>${this.chip("new_tier")}</td><td>You clear a higher tier than before in a skill area</td><td class="mx-num">+${num(R.new_tier_in_area_bonus)}</td></tr>
               <tr><td>${this.chip("pack")}</td><td>You finish every tab of a pack</td><td class="mx-num">+${esc(R.pack_complete_bonus_pct)}% of that run's tab XP</td></tr>
               <tr><td>${this.chip("daily")}</td><td>Your first finished run of today's challenge pack</td><td class="mx-num">its tab XP again (${esc(R.daily_multiplier || 2)}x in all)</td></tr>
               <tr><td>${this.chip("skill_met")}</td><td>A skill on your skill map turns Met (once per skill)</td><td class="mx-num">${num(R.skill_met_per_tier)} x the skill's tier; x${esc(R.skill_met_focus_multiplier)} if it is this week's focus</td></tr>
               <tr><td>${this.chip("streak")}</td><td>Your first clean clear on each day of a streak (below)</td><td class="mx-num">up to +${num(per * cap)} a day</td></tr>
            </tbody></table>
         </div>

         <div class="mx-card">
            <h2>3. The daily streak</h2>
            <p>A streak day is any Arizona calendar day (midnight to midnight) on which you clear <strong>at least one tab cleanly</strong>, in any pack. The bonus is paid once a day, on that day's first clean clear.</p>
            <div class="mx-scroll"><table class="cd-table mx-streak"><thead><tr><th>Day in a row</th>${streakRows.map(([d]) => `<th class="mx-num">${d}${d === cap + 1 ? "+" : ""}</th>`).join("")}</tr></thead>
               <tbody><tr><td>Bonus XP</td>${streakRows.map(([, x]) => `<td class="mx-num">${x ? `+${num(x)}` : "0"}</td>`).join("")}</tr></tbody></table></div>
            <ul class="mx-list">
               <li>Day 1 pays nothing; the bonus starts on your second day in a row and stops growing at day ${cap}.</li>
               <li>Seven days in a row earns ${num(streakRows.slice(0, 7).reduce((t, [, x]) => t + x, 0))} streak XP; every day after that adds ${num(per * cap)}.</li>
               <li>Miss a whole day and the streak starts over at day 1.</li>
               <li>These do <strong>not</strong> keep a streak: opening a pack without clearing a tab, a tab where you opened the guide or a check failed, regular assignments, logged offline time, or coach awards.</li>
               <li>A tab cleared at 12:10 AM counts for the new day.</li>
            </ul>
         </div>

         ${team ? `<div class="mx-card">
            <h2>4. Team awards</h2>
            ${R.weekly_awards ? `<p><strong>Weekly awards.</strong> When a week ends, the top 3 on the weekly board get ${R.weekly_awards.top.map(num).join(" / ")} XP and every member of the top squad gets ${num(R.weekly_awards.squad)}. A coach awards them at Friday practice. Ties share the place, and zero XP never wins.</p>` : ""}
            ${R.coach_awards ? `<p><strong>Coach awards.</strong></p><table class="cd-table"><tbody>${Object.entries(R.coach_awards).sort((x, y) => (x[0] === "other") - (y[0] === "other")).map(([, c]) =>
               `<tr><td>${esc(c.label)}</td><td class="mx-num">+${num(c.points)}${c.once_per_day ? " (once a day)" : ""}</td></tr>`).join("")}</tbody></table>` : ""}
            ${R.gear ? `<p><strong>Gear${R.gear_tentative ? " (tentative)" : ""}.</strong> ${R.gear.map((g) => `${esc(g.label)}: ${num(g.xp)} lifetime XP${g.fridays ? ` and ${num(g.fridays)} Friday practices` : ""}`).join("; ")}.</p>` : ""}
         </div>` : ""}

         <div class="mx-card">
            <h2>${team ? "5" : "4"}. Levels, weeks and seasons</h2>
            <p>Your level comes from all the XP you have ever earned. Level <em>L</em> takes ${num(f)} x <em>L</em> x (<em>L</em> + 1) XP, so each level takes a little more than the one before.</p>
            <table class="cd-table"><thead><tr><th>Title</th><th class="mx-num">From level</th><th class="mx-num">XP needed</th></tr></thead><tbody>
               ${(R.titles || []).map(([lv, t]) => `<tr${t === this.data.profile.title ? ` class="mx-me"` : ""}><td>${esc(t)}${t === this.data.profile.title ? ` <span class="mx-you">you</span>` : ""}</td><td class="mx-num">${num(lv)}</td><td class="mx-num">${num(lvlXp(lv))}</td></tr>`).join("")}</tbody></table>
            <ul class="mx-list">
               <li>The <strong>weekly board</strong> runs ${dow} to ${dowEnd}, Arizona time. Weekly prizes do not count toward the next week's board.</li>
               <li>The <strong>season board</strong> counts this semester (${esc(h.season_name)}). Levels never reset.</li>
               <li>XP does not affect your grade.</li>
            </ul>
         </div>`;
   }
}

customElements.define("app-my-xp", AppMyXp);
